import { usePostHog } from 'posthog-js/react';
import { InspectionFormData } from '@/types/inspection-form.js';
import { useCreateInspection } from '@/hooks/use-inspection-mutations.js';
import { captureException, addBreadcrumb } from '@/lib/sentry.js';
import {
  getAllMediaItems,
  transformToCreateInspectionRequest,
} from '@/features/inspection-form/utils/transform.js';
import { uploadMediaBatch } from '@/features/inspection-form/services/media-upload.js';

export interface SubmitOptions {
  onProgress?: (id: string, progress: number) => void;
  onSetTotal?: (total: number) => void;
}

export function useSubmitInspection() {
  const createInspection = useCreateInspection();
  const posthog = usePostHog();

  const submit = async (
    formData: InspectionFormData,
    taskId: string,
    propertyId: string,
    options: SubmitOptions = {},
  ): Promise<void> => {
    const { onProgress, onSetTotal } = options;
    const inspectionId = `inspection-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    const allMedia = getAllMediaItems(formData);
    onSetTotal?.(allMedia.length);

    if (allMedia.length > 0) {
      addBreadcrumb('Starting media upload', 'inspection', {
        taskId,
        propertyId,
        mediaCount: allMedia.length,
      });
      try {
        const uploadedItems = await uploadMediaBatch(
          allMedia,
          propertyId,
          inspectionId,
          onProgress ?? (() => {}),
        );
        uploadedItems.forEach((uploaded, i) => {
          allMedia[i].url = uploaded.url;
          allMedia[i].id = uploaded.id;
        });
      } catch (uploadError) {
        captureException(uploadError, { taskId, propertyId, mediaCount: allMedia.length });
        throw uploadError;
      }
    }

    addBreadcrumb('Submitting inspection report', 'inspection', { taskId, propertyId });
    const request = transformToCreateInspectionRequest(formData, taskId, propertyId, inspectionId);
    await createInspection.mutateAsync({ request, taskId, propertyId });
    posthog.capture('inspection_submitted', {
      task_id: taskId,
      property_id: propertyId,
      media_count: allMedia.length,
    });
  };

  return { submit, isSubmitting: createInspection.isPending };
}
